import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Box, Typography, TextField, Button, Divider, Card, Grid, Chip } from "@mui/material";

import LayoutCustomer from "../components/LayoutCustomer";

import { API_BASE_URL } from "../config.ts";

type PublicOrder = {
    id: string;
    state: string;
    assignedTo: string;
    notes: string;
    missingInfo: string;
    internalNumber: string;
    createdDateTime: string;
    orderDetails: {
        lineNumber: number;
        customerNumber: string;
        containerNumber: string;
        customsReferenceNumber: string;
        state: string;
        ata: string;
        atc: string;
        mrn: string;
        etaDate: {
            year: number;
            month: number;
            day: number;
            dayOfWeek: number;
        } | null;
        //etaDate: Date | null;
        etaIntervalStart: {
            hour: number;
            minute: number;
            second: number;
        } | null;
        etaIntervalEnd: {
            hour: number;
            minute: number;
            second: number;
        } | null;
        positionsNumber: number;
        notes: string;
    }[];
    invoices: {
        invoiceId: string;
        accountingInvoiceNumber: string;
        amount: number;
        isPaid: boolean;
    }[];
};

export default function OrderDetailsCustomerPage() {
    const { t } = useTranslation();

    const { orderId } = useParams();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [order, setOrder] = useState<PublicOrder | null>(null);
    const [sent, setSent] = useState(false);

    function formatEtaDate(
        date: { year: number; month: number; day: number } | null
    ): string {
        if (!date) return "-";
        const d = new Date(date.year, date.month - 1, date.day);
        return d.toLocaleDateString(); // user’s locale
    }

    function formatEtaTime(
        date: { year: number; month: number; day: number } | null,
        time: { hour: number; minute: number; second?: number } | null
    ): string {
        if (!date || !time) return "-";
        const d = new Date(
            date.year,
            date.month - 1,
            date.day,
            time.hour,
            time.minute,
            time.second ?? 0
        );
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleTimeString(); // user’s locale
    }


    const requestCode = async () => {
        const res = await fetch(`${API_BASE_URL}/orders/${orderId}/access/request-code`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(email),
        });
        if (res.ok) setSent(true);
        else alert("Failed to send code.");
    };

    const verifyCode = async () => {
        try {
            // Step 1: verify code
            const res = await fetch(`${API_BASE_URL}/orders/${orderId}/access/verify-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, code }),
            });

            if (!res.ok) {
                alert(t("Invalid or expired code"));
                return;
            }

            localStorage.setItem("orderCode", code);
            localStorage.setItem("orderEmail", email);

            // Step 2: fetch order info from public endpoint
            const orderRes = await fetch(`${API_BASE_URL}/orders/${orderId}/customer?code=${code}`);
            if (!orderRes.ok) {
                alert(t("Failed to load order information"));
                return;
            }

            const orderData = await orderRes.json();
            setOrder(orderData);
        } catch (err) {
            console.error("Error verifying code", err);
            alert(t("Something went wrong"));
        }
    };

    useEffect(() => {
        const autoVerify = async () => {
            const savedCode = localStorage.getItem("orderCode");
            const savedEmail = localStorage.getItem("orderEmail");

            if (savedCode && savedEmail && orderId) {
                try {
                    // Fetch order info directly from public endpoint
                    const res = await fetch(
                        `${API_BASE_URL}/orders/${orderId}/customer?code=${savedCode}`
                    );

                    if (!res.ok) throw new Error("Invalid or expired code");

                    const data = await res.json();
                    setOrder(data); // show order again
                } catch (err) {
                    console.error("Auto verify failed", err);
                    localStorage.removeItem("orderCode");
                    localStorage.removeItem("orderEmail");
                }
            }
        };

        autoVerify();
    }, [orderId]);

    if (order) {
        return (
            <LayoutCustomer>
                <Box sx={{ p: 3 }}>
                    <Card sx={{ mb: 3, p: 2 }}>
                        <Typography variant="h5">{t("Order")} {order.internalNumber}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("Created On")}: {new Date(order.createdDateTime).toLocaleString()}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}><strong>{t("State")}:</strong> {order.state || "-"}</Grid>
                            <Grid item xs={6}><strong>{t("Assigned To")}:</strong> {order.assignedTo || "-"}</Grid>
                            <Grid item xs={6}><strong>{t("Missing Info")}:</strong> {order.missingInfo || "-"}</Grid>
                        </Grid>
                    </Card>

                    <Typography variant="h6" sx={{ mb: 1 }}>{t("Details")}</Typography>
                    <Grid container spacing={2}>
                        {order.orderDetails
                            ?.sort((a, b) => a.lineNumber - b.lineNumber)
                            .map((d, idx) => (
                                <Grid item xs={12} md={6} key={idx}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            {t("Line")} {d.lineNumber}
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />

                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("State")}:</strong> {d.state || "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("Customer Reference Number")}:</strong> {d.customerNumber || "-"}</Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("Container Number")}:</strong> {d.containerNumber || "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("Customs Ref")}:</strong> {d.customsReferenceNumber || "-"}</Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("ATA")}:</strong> {d.ata || "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("Position number")}:</strong> {d.positionsNumber}</Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("ATC")}:</strong> {d.atc || "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("MRN")}:</strong> {d.mrn}</Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography variant="body2"><strong>{t("ETA Date")}:</strong> {formatEtaDate(d.etaDate)}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">
                                                    <strong>{t("Start")} - {t("End")}:</strong>{" "}
                                                    {formatEtaTime(d.etaDate, d.etaIntervalStart)} → {formatEtaTime(d.etaDate, d.etaIntervalEnd)}
                                                </Typography>
                                            </Grid>

                                        </Grid>
                                    </Card>
                                </Grid>

                            ))}
                    </Grid>

                    <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>{t("Invoices")}</Typography>
                    {order.invoices?.length ? (
                        <Grid container spacing={2}>
                            {order.invoices.map((inv, idx) => (
                                <Grid item xs={12} md={6} key={idx}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle1">
                                            {t("Invoice")} {inv.accountingInvoiceNumber || inv.invoiceId}
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2">
                                            {t("Amount")}: {inv.amount?.toLocaleString(undefined, { style: "currency", currency: "EUR" })}
                                        </Typography>
                                        <Chip
                                            label={inv.isPaid ? t("Paid") : t("Not Paid")}
                                            color={inv.isPaid ? "success" : "error"}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                            <Typography variant="body2">{t("No invoices available")}</Typography>
                    )}
                </Box>
            </LayoutCustomer>
        );
    }

    return (
        <LayoutCustomer>
            <Box sx={{ maxWidth: 400, mx: "auto", mt: 5, p: 2 }}>
                <Typography variant="h5" gutterBottom>
                    View Order
                </Typography>

                <TextField
                    fullWidth
                    label="Your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                />

                <TextField
                    fullWidth
                    label="Enter Access Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    margin="normal"
                />

                {!sent && (
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={requestCode}
                        sx={{ mt: 2 }}
                    >
                        Send Code
                    </Button>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={verifyCode}
                    sx={{ mt: 2 }}
                >
                    Verify Code
                </Button>

                
            </Box>
        </LayoutCustomer>
    );
}
